import Layout from "@/components/Layout";
import { lazy, Suspense } from "react";
import { Link } from "react-router-dom";
import { Eye, Brain, Moon, Heart, Shield, Truck, Headphones, Star, ChevronRight, Zap, Sparkles, Wind, ThermometerSun, Check, Lock, ShieldCheck, CreditCard, Hand, Activity, Battery, Award, Users, Globe } from "lucide-react";

const AnimatedSection = lazy(() => import("@/components/AnimatedSection"));
import productShowcase2 from "@/assets/product-showcase-2.webp";

import shiatsuHero from "@/assets/shiatsu-hero.webp";
import paymentIcons from "@/assets/payment-badges-new.jpg";

const Index = () => {
  return (
    <Layout>
      {/* Hero */}
      <section className="relative min-h-[95vh] flex items-center overflow-hidden gradient-dark">
        <div className="absolute inset-0 opacity-20" style={{ background: "radial-gradient(circle at 70% 50%, hsl(152 55% 28% / 0.5), transparent 60%)" }} />
        <div className="container mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center py-20 relative z-10">
          <div className="order-2 lg:order-1">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-cta/10 border border-cta/20 mb-6">
              <Sparkles size={14} className="text-cta" />
              <span className="text-xs font-semibold text-cta tracking-wide uppercase">Premium Wellness Technology</span>
            </div>
            <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl font-normal leading-[1.1] text-white mb-6 text-balance">
              Your Daily Ritual for Deep Relief & Renewal
            </h1>
            <p className="text-lg text-white/60 leading-relaxed mb-8 max-w-lg">
              From tired eyes to tense shoulders — Luminaease combines advanced massage therapy, soothing heat, and ergonomic precision to restore your body and calm your mind.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 mb-8">
              <Link to="/shiatsu" className="inline-flex items-center justify-center gap-2 px-8 py-4 text-sm font-semibold rounded-full gradient-cta text-white hover:opacity-90 transition-opacity shadow-cta">
                Shop DeepRelief Massager™ <ChevronRight size={16} />
              </Link>
              <Link to="/product" className="inline-flex items-center justify-center px-8 py-4 text-sm font-semibold rounded-full border border-white/20 text-white hover:bg-white/10 transition-colors">
                Shop Smart Eye Massager™
              </Link>
            </div>
            <div className="flex items-center gap-6 text-white/50 text-sm">
              <div className="flex items-center gap-1.5"><Check size={14} className="text-cta" /> Free UK Shipping 🇬🇧</div>
              <div className="flex items-center gap-1.5"><Check size={14} className="text-cta" /> 30-Day Guarantee</div>
              <div className="flex items-center gap-1.5"><Check size={14} className="text-cta" /> 24/7 Support</div>
            </div>
          </div>
          <div className="order-1 lg:order-2 flex justify-center">
            <div className="relative w-full max-w-md lg:max-w-lg aspect-square">
              <div className="absolute inset-0 rounded-3xl shadow-glow scale-90" />
              <img src="/hero-deeprelief.webp" alt="Luminaease DeepRelief Massager™ — neck massage in action" width={1080} height={1080} fetchPriority="high" className="w-full h-full object-contain relative z-10 drop-shadow-2xl" />
            </div>
          </div>
        </div>
      </section>

      {/* Social Proof Bar */}
      <div className="bg-secondary py-4 border-b border-border">
        <div className="container mx-auto px-6 flex flex-wrap justify-center items-center gap-8 text-sm text-muted-foreground">
          <div className="flex items-center gap-2">
            <div className="flex gap-0.5">{Array.from({ length: 5 }).map((_, i) => <Star key={i} size={14} className="fill-warm text-warm" />)}</div>
            <span className="font-semibold text-foreground">4.9/5</span> from 4,700+ reviews
          </div>
          <div className="hidden sm:block w-px h-4 bg-border" />
          <span>🔥 <strong className="text-foreground">20,000+</strong> units sold</span>
          <div className="hidden sm:block w-px h-4 bg-border" />
          <span>⚡ Recommended by <strong className="text-foreground">500+</strong> wellness professionals</span>
        </div>
      </div>

      <Suspense fallback={<div />}>
      {/* Brand Authority */}
      <AnimatedSection className="py-24 bg-background">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-xs font-bold tracking-widest uppercase text-primary">The Luminaease Difference</span>
            <h2 className="font-serif text-3xl md:text-5xl text-foreground mt-4 mb-6">
              Wellness Technology Designed for Modern Life
            </h2>
            <p className="text-lg text-muted-foreground leading-relaxed">
              We believe recovery shouldn't be complicated. Luminaease brings professional-grade massage therapy into your daily routine — combining advanced engineering with a calm, intentional wellness experience.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Heart, title: "Stress Relief", desc: "Melt away the tension that builds from screens, commutes, and daily demands." },
              { icon: Sparkles, title: "Deep Relaxation", desc: "Heat therapy, kneading, and compression work together for therapeutic calm." },
              { icon: Moon, title: "Better Sleep", desc: "Wind down with a 15-minute ritual that prepares your body and mind for rest." },
              { icon: Zap, title: "Daily Recovery", desc: "Restore tired eyes and stiff muscles — feel renewed every single day." },
            ].map((b, i) => (
              <div key={i} className="p-6 rounded-2xl bg-card border border-border hover:border-primary/30 hover:shadow-glow transition-all duration-300 text-center group">
                <div className="w-14 h-14 rounded-xl bg-accent flex items-center justify-center mx-auto mb-5 group-hover:gradient-primary transition-all">
                  <b.icon size={24} className="text-primary group-hover:text-white transition-colors" />
                </div>
                <h3 className="font-serif text-lg text-foreground mb-2">{b.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </AnimatedSection>

      {/* Featured Products */}
      <AnimatedSection className="py-24 bg-secondary">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <span className="text-xs font-bold tracking-widest uppercase text-primary">Our Collection</span>
            <h2 className="font-serif text-3xl md:text-5xl text-foreground mt-4">Premium Wellness Devices</h2>
            <p className="text-muted-foreground text-lg mt-4">Two expertly engineered solutions for complete body relief.</p>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {/* DeepRelief Massager Card — Bestseller */}
            <div className="bg-card rounded-3xl border-2 border-primary/30 overflow-hidden hover:shadow-lg transition-all group ring-1 ring-primary/10">
              <div className="relative h-72 overflow-hidden bg-secondary">
                <img src={shiatsuHero} alt="Luminaease DeepRelief Massager™" width={420} height={420} loading="lazy" className="w-full h-full object-contain p-6 group-hover:scale-105 transition-transform duration-500" />
                <div className="absolute top-4 left-4 px-3 py-1.5 rounded-full gradient-cta text-white text-xs font-bold shadow-cta">🔥 Bestseller</div>
              </div>
              <div className="p-8">
                <div className="flex items-center gap-2 mb-3">
                  <div className="flex gap-0.5">{Array.from({ length: 5 }).map((_, i) => <Star key={i} size={14} className="fill-warm text-warm" />)}</div>
                  <span className="text-sm text-muted-foreground">4.8 (1,923 reviews)</span>
                </div>
                <h3 className="font-serif text-2xl text-foreground mb-3">Luminaease DeepRelief Massager™</h3>
                <p className="text-sm text-muted-foreground leading-relaxed mb-4">
                  Dissolve neck and shoulder tension with human-like kneading, red light heat therapy, and ergonomic precision — professional-grade relief anywhere.
                </p>
                <ul className="space-y-2 mb-6">
                  {["Deep Kneading + Heat Therapy", "5 Modes, 0.76 kg Ultralight", "12-Day Battery, USB-C Charging"].map((f, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Check size={14} className="text-primary shrink-0" /> {f}
                    </li>
                  ))}
                </ul>
                <div className="flex items-center justify-between">
                  <div>
                    <span className="text-2xl font-bold text-foreground">£64.99</span>
                    <span className="text-sm text-muted-foreground line-through ml-2">£119.99</span>
                  </div>
                  <Link to="/shiatsu" className="inline-flex items-center gap-2 px-6 py-3 text-sm font-semibold rounded-full gradient-cta text-white hover:opacity-90 transition-opacity shadow-cta">
                    Shop Now <ChevronRight size={14} />
                  </Link>
                </div>
              </div>
            </div>

            {/* Eye Massager Card */}
            <div className="bg-card rounded-3xl border border-border overflow-hidden hover:shadow-lg transition-all group">
              <div className="relative h-72 overflow-hidden bg-secondary">
                <img src={productShowcase2} alt="Luminaease Eye Massager" width={420} height={420} loading="lazy" className="w-full h-full object-contain p-6 group-hover:scale-105 transition-transform duration-500" />
                <div className="absolute top-4 left-4 px-3 py-1.5 rounded-full bg-warm text-white text-xs font-bold">Popular</div>
              </div>
              <div className="p-8">
                <div className="flex items-center gap-2 mb-3">
                  <div className="flex gap-0.5">{Array.from({ length: 5 }).map((_, i) => <Star key={i} size={14} className="fill-warm text-warm" />)}</div>
                  <span className="text-sm text-muted-foreground">4.9 (2,847 reviews)</span>
                </div>
                <h3 className="font-serif text-2xl text-foreground mb-3">Luminaease™ Eye Massager</h3>
                <p className="text-sm text-muted-foreground leading-relaxed mb-4">
                  Relieve digital eye strain with heat therapy, acupoint massage, air compression, and Bluetooth audio — your 15-minute ritual for deep eye recovery.
                </p>
                <ul className="space-y-2 mb-6">
                  {["Heat + Air Compression + Vibration", "5 Intelligent Massage Modes", "Bluetooth Audio & 120min Battery"].map((f, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Check size={14} className="text-primary shrink-0" /> {f}
                    </li>
                  ))}
                </ul>
                <div className="flex items-center justify-between">
                  <div>
                    <span className="text-2xl font-bold text-foreground">£69.99</span>
                    <span className="text-sm text-muted-foreground line-through ml-2">£129.99</span>
                  </div>
                  <Link to="/product" className="inline-flex items-center gap-2 px-6 py-3 text-sm font-semibold rounded-full gradient-cta text-white hover:opacity-90 transition-opacity shadow-cta">
                    Shop Now <ChevronRight size={14} />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </AnimatedSection>

      {/* Benefits / Lifestyle */}
      <AnimatedSection className="py-24 gradient-navy text-white">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-xs font-bold tracking-widest uppercase text-cta">Why Luminaease</span>
            <h2 className="font-serif text-3xl md:text-5xl text-white mt-4 mb-6">
              Real Results You Can Feel
            </h2>
            <p className="text-white/60 text-lg leading-relaxed">
              Thousands of customers report life-changing improvements in comfort, sleep, and daily well-being.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {[
              { icon: Eye, title: "Eye Strain Relief", desc: "Soothe tired, dry eyes after hours of screen time with targeted heat and acupoint massage." },
              { icon: Hand, title: "Neck & Shoulder Recovery", desc: "Dissolve deep-seated muscle knots with human-like kneading and red light heat therapy." },
              { icon: ThermometerSun, title: "Therapeutic Heat", desc: "Precision-calibrated warmth that penetrates tight muscles for accelerated relaxation." },
              { icon: Moon, title: "Better Sleep Quality", desc: "Wind down naturally — prepare your body and mind for deep, restorative rest." },
              { icon: Activity, title: "5+ Intelligent Modes", desc: "Personalized programs from gentle relaxation to deep tissue intensity." },
              { icon: Battery, title: "All-Day Battery", desc: "Long-lasting rechargeable batteries so relief is always within reach." },
            ].map((b, i) => (
              <div key={i} className="p-6 rounded-2xl bg-white/5 border border-white/10 hover:bg-white/10 transition-all">
                <div className="w-12 h-12 rounded-xl bg-white/10 flex items-center justify-center mb-4">
                  <b.icon size={22} className="text-primary" />
                </div>
                <h3 className="font-semibold text-white text-base mb-2">{b.title}</h3>
                <p className="text-sm text-white/60 leading-relaxed">{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </AnimatedSection>

      {/* Brand Authority — NEW */}
      <AnimatedSection className="py-24 bg-background">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-xs font-bold tracking-widest uppercase text-primary">Trusted Across the UK</span>
            <h2 className="font-serif text-3xl md:text-5xl text-foreground mt-4 mb-6">
              Chosen by Wellness Professionals & Everyday People Alike
            </h2>
            <p className="text-lg text-muted-foreground leading-relaxed">
              Luminaease is trusted by physical therapists, personal trainers, and remote workers across 30+ countries. Our devices are backed by real results and real reviews.
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto">
            {[
              { value: "20,000+", label: "Devices Sold", icon: Award },
              { value: "4.9/5", label: "Average Rating", icon: Star },
              { value: "500+", label: "Pro Recommendations", icon: Users },
              { value: "30+", label: "Countries Served", icon: Globe },
            ].map((s, i) => (
              <div key={i} className="text-center p-6 rounded-2xl bg-card border border-border">
                <s.icon size={24} className="text-primary mx-auto mb-3" />
                <p className="text-2xl font-bold text-foreground mb-1">{s.value}</p>
                <p className="text-xs text-muted-foreground font-semibold uppercase tracking-wide">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </AnimatedSection>

      {/* Transformation Story — NEW */}
      <AnimatedSection className="py-24 bg-secondary">
        <div className="container mx-auto px-6 lg:px-12 max-w-5xl">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="text-xs font-bold tracking-widest uppercase text-primary">Your Wellness Journey</span>
            <h2 className="font-serif text-3xl md:text-5xl text-foreground mt-4 mb-6">
              From Daily Tension to Daily Renewal
            </h2>
            <p className="text-lg text-muted-foreground leading-relaxed">
              Whether it's digital eye fatigue or chronic neck pain, Luminaease transforms your recovery routine into a calming 15-minute ritual that delivers real, lasting comfort.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { step: "01", title: "Identify Your Tension", desc: "Screen fatigue, neck stiffness, shoulder knots, or restless sleep — Luminaease targets the source of your discomfort.", icon: Brain },
              { step: "02", title: "Choose Your Device", desc: "The Eye Massager for digital eye recovery, or the DeepRelief Massager™ for neck, shoulder, and muscle relief.", icon: Sparkles },
              { step: "03", title: "Feel the Difference", desc: "In just 15 minutes, heat therapy, precision massage, and intelligent modes dissolve tension and restore your calm.", icon: Heart },
            ].map((s, i) => (
              <div key={i} className="relative text-center p-8 rounded-2xl bg-card border border-border hover:border-primary/30 hover:shadow-glow transition-all group">
                <span className="absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full gradient-primary text-white text-xs font-bold shadow-cta">Step {s.step}</span>
                <div className="w-16 h-16 rounded-2xl bg-accent flex items-center justify-center mx-auto mb-6 mt-2 group-hover:gradient-primary transition-all">
                  <s.icon size={28} className="text-primary group-hover:text-white transition-colors" />
                </div>
                <h3 className="font-serif text-xl text-foreground mb-3">{s.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </AnimatedSection>

      {/* Social Proof */}
      <AnimatedSection className="py-24 bg-background">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <span className="text-xs font-bold tracking-widest uppercase text-primary">Trusted by Thousands</span>
            <h2 className="font-serif text-3xl md:text-5xl text-foreground mt-4">What Our Customers Say</h2>
            <div className="flex justify-center items-center gap-2 mt-4">
              <div className="flex gap-0.5">{Array.from({ length: 5 }).map((_, i) => <Star key={i} size={18} className="fill-warm text-warm" />)}</div>
              <span className="font-bold text-foreground">4.9/5</span>
              <span className="text-muted-foreground">from 4,700+ verified reviews</span>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { name: "Sarah M.", role: "UX Designer", text: "After 10-hour design sprints, the eye massager is the reset my eyes desperately need. I sleep better and wake up refreshed.", rating: 5 },
              { name: "James K.", role: "Software Engineer", text: "I was skeptical, but after two weeks my tension headaches have almost disappeared. A genuine wellness investment.", rating: 5 },
              { name: "Angela P.", role: "Office Manager", text: "The DeepRelief Massager™ has been a game-changer for my chronic neck pain. I use it every evening and the relief is incredible.", rating: 5 },
              { name: "Marco R.", role: "Remote Worker", text: "The eye massager is my afternoon ritual. 15 minutes of pure calm that makes the rest of my workday so much more productive.", rating: 5 },
            ].map((t, i) => (
              <div key={i} className="bg-card p-6 rounded-2xl border border-border hover:shadow-lg transition-shadow">
                <div className="flex gap-0.5 mb-4">
                  {Array.from({ length: t.rating }).map((_, j) => (
                    <Star key={j} size={14} className="fill-warm text-warm" />
                  ))}
                </div>
                <p className="text-sm text-muted-foreground leading-relaxed mb-4">"{t.text}"</p>
                <div className="border-t border-border pt-4">
                  <p className="text-sm font-semibold text-foreground">{t.name}</p>
                  <p className="text-xs text-muted-foreground">{t.role}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </AnimatedSection>

      {/* Trust & Assurance */}
      <AnimatedSection className="py-16 bg-secondary">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-6 max-w-5xl mx-auto text-center">
            {[
              { icon: ShieldCheck, title: "30-Day Money Back", desc: "Not satisfied? Full refund, no questions asked." },
              { icon: Truck, title: "Free UK Shipping 🇬🇧", desc: "Fast delivery in 5–7 business days across the United Kingdom." },
              { icon: Lock, title: "Secure Checkout", desc: "256-bit SSL encrypted. Your data is safe." },
              { icon: Headphones, title: "24/7 Support", desc: "Our wellness team is here for you, always." },
            ].map((t, i) => (
              <div key={i} className="p-6 rounded-2xl bg-card border border-border">
                <div className="w-12 h-12 mx-auto mb-4 rounded-xl gradient-primary flex items-center justify-center">
                  <t.icon size={22} className="text-white" />
                </div>
                <h3 className="font-serif text-base text-foreground mb-1">{t.title}</h3>
                <p className="text-xs text-muted-foreground">{t.desc}</p>
              </div>
            ))}
          </div>
          <div className="flex items-center justify-center mt-8">
            <img src={paymentIcons} alt="Accepted payment methods — Amazon, Amex, Apple Pay, Bancontact, Discover, Mastercard, Shop Pay, Visa" width={400} height={100} loading="lazy" className="h-16 object-contain" />
          </div>
          <p className="text-center text-xs text-muted-foreground mt-3">Secure Payments • Encrypted Checkout • 30-Day Risk-Free Guarantee</p>
        </div>
      </AnimatedSection>

      {/* Final CTA */}
      <section className="py-24 gradient-dark relative overflow-hidden">
        <div className="absolute inset-0 opacity-30" style={{ background: "radial-gradient(circle at 50% 50%, hsl(152 55% 28% / 0.5), transparent 60%)" }} />
        <div className="container mx-auto px-6 lg:px-12 text-center max-w-2xl relative z-10">
          <h2 className="font-serif text-3xl md:text-5xl text-white mb-6">Begin Your Wellness Journey Today</h2>
          <p className="text-white/60 text-lg leading-relaxed mb-8">
            Join thousands who've made Luminaease part of their daily recovery ritual. From tired eyes to tense shoulders — relief starts here.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-6">
            <Link to="/product" className="inline-flex items-center gap-2 px-10 py-4 text-sm font-semibold rounded-full gradient-cta text-white hover:opacity-90 transition-opacity shadow-cta">
              Eye Massager — £69.99 <ChevronRight size={16} />
            </Link>
            <Link to="/shiatsu" className="inline-flex items-center gap-2 px-10 py-4 text-sm font-semibold rounded-full border border-white/20 text-white hover:bg-white/10 transition-colors">
              DeepRelief Massager™ — £64.99 <ChevronRight size={16} />
            </Link>
          </div>
          <div className="flex flex-wrap justify-center items-center gap-4 text-white/40 text-xs">
            <span className="flex items-center gap-1"><Lock size={12} /> Secure Checkout</span>
            <span className="flex items-center gap-1"><Truck size={12} /> Free Shipping</span>
            <span className="flex items-center gap-1"><Shield size={12} /> 30-Day Guarantee</span>
          </div>
        </div>
      </section>
      </Suspense>
    </Layout>
  );
};

export default Index;
